/**
 * Reading a page's print: once per page, and off the main thread when it can.
 *
 * The viewer needs to know where the printer put each value before it can
 * draw the live one in its place (`printedLayout`). That used to run on the
 * main thread in the image's load handler, over a full copy of the page's
 * pixels, before the page was allowed to appear - so on a tablet a pushed
 * document sat behind a grey shimmer for as long as the arithmetic took, and
 * again on every Back and Next.
 *
 * Now the page shows as soon as its image is decoded - the scan already has
 * the printed values in exactly the places the live ones will go - and:
 *
 * - the reading runs in a worker, started as soon as the page is known, in
 *   parallel with the image download;
 * - the answer is kept per page and per geometry, so a page is read once;
 * - the viewer asks for the other pages as soon as the one on screen is done,
 *   so turning the page finds its answer, and its image, already there.
 *
 * Without a worker (jsdom, a very old browser) the same reading runs on the
 * main thread after the page has painted, which is the old behaviour minus
 * the full-page copies.
 */

import {
  erasePatches,
  fallbackLayout,
  inpaintFills,
  layoutPage,
  type Box,
  type FontMetrics,
  type PageLayout,
  type RowFill,
} from '@/components/domain/printedLayout'
import { readRaster } from '@/components/domain/printedPage'

export interface PageGeometry {
  text: { key: string; box: Box }[]
  ticks: { key: string; box: Box }[]
}

export interface PageAnalysis {
  layout: PageLayout
  /** The rows to paper over, in page pixels; null when the pixels could not be read. */
  fills: RowFill[] | null
  width: number
  height: number
}

export interface AnalysisRequest {
  id: number
  url: string
  geometry: PageGeometry
  metrics: FontMetrics
}

export type AnalysisResponse =
  | { id: number; ok: true; analysis: PageAnalysis }
  | { id: number; ok: false; error: string }

// ---------------------------------------------------------------- worker

let worker: Worker | null | undefined
let nextId = 1
const pending = new Map<number, (analysis: PageAnalysis | null) => void>()

/** Whether reading can happen off the main thread here. Synchronous, and settled once. */
export function workerAvailable(): boolean {
  return connect() !== null
}

function connect(): Worker | null {
  if (worker !== undefined) return worker
  if (
    typeof Worker === 'undefined' ||
    typeof OffscreenCanvas === 'undefined' ||
    typeof createImageBitmap === 'undefined'
  ) {
    worker = null
    return worker
  }
  try {
    worker = new Worker(new URL('./pageAnalysis.worker.ts', import.meta.url), { type: 'module' })
  } catch {
    worker = null
    return worker
  }
  worker.onmessage = (event: MessageEvent<AnalysisResponse>) => {
    const settle = pending.get(event.data.id)
    pending.delete(event.data.id)
    settle?.(event.data.ok ? event.data.analysis : null)
  }
  worker.onerror = () => {
    // A worker that cannot start (a blocked script, no module workers) fails
    // everything asked of it, and the main thread takes over for good.
    for (const settle of pending.values()) settle(null)
    pending.clear()
    worker?.terminate()
    worker = null
  }
  return worker
}

/** Start the worker ahead of the first page, so its start-up is not on that page's clock. */
export function warmPageAnalysis() {
  connect()
}

// ---------------------------------------------------------------- cache

/** Answers in flight or settled, by page image and geometry. Small: no pixels. */
const answers = new Map<string, Promise<PageAnalysis | null>>()
/** The same answers once they are in, so a revisited page draws in its first frame. */
const settled = new Map<string, PageAnalysis>()
const MAX_ANSWERS = 48

function bounded<V>(map: Map<string, V>) {
  if (map.size > MAX_ANSWERS) map.delete(map.keys().next().value!)
}

/** A reading already in hand, if there is one. */
export function settledAnalysis(key: string): PageAnalysis | undefined {
  return settled.get(key)
}

export function rememberAnalysis(key: string, analysis: PageAnalysis) {
  settled.set(key, analysis)
  bounded(settled)
}

export function analysisKey(url: string, geometryKey: string) {
  return `${url}\n${geometryKey}`
}

/**
 * The page's reading, from the worker. Resolves null when the worker could
 * not read it (no worker, a failed fetch), and the caller reads the loaded
 * image itself instead.
 */
export function analyseInWorker(
  url: string,
  geometry: PageGeometry,
  geometryKey: string,
  metrics: FontMetrics,
): Promise<PageAnalysis | null> {
  const key = analysisKey(url, geometryKey)
  const known = answers.get(key)
  if (known) return known
  const active = connect()
  if (!active) return Promise.resolve(null)

  const id = nextId++
  const answer = new Promise<PageAnalysis | null>((resolve) => {
    pending.set(id, resolve)
    active.postMessage({ id, url, geometry, metrics } satisfies AnalysisRequest)
  }).then((analysis) => {
    // Do not remember a failure: the next ask (a retried image) may succeed.
    if (analysis) rememberAnalysis(key, analysis)
    else answers.delete(key)
    return analysis
  })
  answers.set(key, answer)
  bounded(answers)
  return answer
}


// ---------------------------------------------------------------- main thread

let readable: boolean | undefined

/** Whether this browser will hand a script a page's pixels at all. Cheap after the first call. */
export function canReadPixels(): boolean {
  if (readable !== undefined) return readable
  try {
    readable = document.createElement('canvas').getContext('2d') !== null
  } catch {
    readable = false
  }
  return readable
}

/** The same reading on this thread, from an image that has loaded. */
export function analyseImage(
  image: HTMLImageElement,
  geometry: PageGeometry,
  metrics: FontMetrics,
  aspect: number,
): PageAnalysis {
  const raster = canReadPixels() ? readRaster(image) : null
  if (!raster) {
    return {
      layout: fallbackLayout(geometry.text, geometry.ticks, aspect, metrics),
      fills: null,
      width: image.naturalWidth,
      height: image.naturalHeight,
    }
  }
  const layout = layoutPage(raster, geometry.text, geometry.ticks, metrics)
  return {
    layout,
    fills: inpaintFills(raster, erasePatches(layout, geometry.text)),
    width: raster.width,
    height: raster.height,
  }
}

// ---------------------------------------------------------------- images

/** Decoded images of pages not yet on screen, held so the browser keeps them. */
const preloaded = new Map<string, HTMLImageElement>()

/** Fetch and decode a page image ahead of time, so turning to it is instant. */
export function preloadImage(url: string) {
  if (preloaded.has(url) || typeof Image === 'undefined') return
  const image = new Image()
  image.decoding = 'async'
  image.src = url
  void image.decode?.().catch(() => preloaded.delete(url))
  preloaded.set(url, image)
  if (preloaded.size > 12) preloaded.delete(preloaded.keys().next().value!)
}
